import { NextPage } from 'next'
import { useTranslation } from 'next-i18next'
import { useMemo } from 'react'
import { HtmlPageMeta } from '~/components/HtmlPageMeta/HtmlPageMeta'
import { HtmlPageMetaResourceService } from '~/components/HtmlPageMeta/HtmlPageMetaResourceService'
import { FeaturedOrganizers } from '~/components/FeaturedOrganizers/FeaturedOrganizers'
import { FeaturedAwards } from '~/components/FeaturedAwards'
import { Faq } from '~/components/Faq/Faq'
import { PolygonBanner } from '~/components/PolygonBanner/PolygonBanner'
import { FeaturedGalleries, FeaturedGalleryDto } from '~/components/FeaturedGalleries'
import { GetOrganizersResponse } from '~/modules/Organizers/Application/GetFeaturedOrganizers/GetOrganizersResponse'
import { HomePageViewModel } from '~/modules/PresentationAdapters/HomePageAdapter'
import styles from './MarketHomePage.module.scss'
import { MuseumContactBanner } from './MuseumContactBanner'
import { MuseumHomeHero } from './MuseumHomeHero'
import { ComponentTrophyDtoTransformer } from '~/modules/Products/Infrastructure/ComponentTrophyDtoTransformer'

export interface MarketHomePageProps {
  viewModel: HomePageViewModel
}

export const MarketHomePage: NextPage<MarketHomePageProps> = ({ viewModel }) => {
  const { t } = useTranslation('home')
  const organizers = useMemo(
    () => GetOrganizersResponse.transform(viewModel.organizers),
    [viewModel.organizers]
  )
  const featuredAwards = useMemo(
    () => (viewModel.featuredAwards ?? []).map((trophy) => ComponentTrophyDtoTransformer.fromApplication(trophy)),
    [viewModel.featuredAwards]
  )

  const galleries: FeaturedGalleryDto[] = [
    {
      name: 'BRUNO CETRARO',
      imageUrl: 'https://cdn.leverade.com/trophyverse/users/ae04da84-298a-4d67-9aa1-609a79346917@1709641938.jpeg',
      galleryUrl: '/gallery/brunocetraro',
    },
    {
      name: 'ERWIN PÉREZ',
      imageUrl: 'https://cdn.leverade.com/trophyverse/users/485e7916-e11d-4966-9a44-228d9ffee37f@1712246700.png',
      galleryUrl: '/gallery/erwinfeuchtmann',
    },
    {
      name: 'SHIRLEY MELÉNDEZ',
      imageUrl: 'https://cdn.leverade.com/trophyverse/users/db329b43-664e-4bb5-bc72-4cbc065d5fdc@1710799545.jpg',
      galleryUrl: '/gallery/shirleymelz',
    },
  ]

  const htmlPageMetaUrlProps = (
    new HtmlPageMetaResourceService(t('home_page_title'), t('home_page_description'))
  ).getProperties()
  const htmlPageMetaProps = { ...viewModel.htmlPageMetaContextProps, ...htmlPageMetaUrlProps }

  return (
    <>
      <HtmlPageMeta { ...htmlPageMetaProps } />

      <div className={ styles.marketHomePage }>
        <MuseumHomeHero />

        <div className={ styles.marketHomePage__content }>
          <div className={ `${styles.marketHomePage__section} ${styles.marketHomePage__galleriesSection}` }>
            <FeaturedGalleries
              featured={ galleries }
              appearance="museumHome"
            />
          </div>

          <div className={ `${styles.marketHomePage__section} ${styles.marketHomePage__awardsSection}` }>
            <FeaturedAwards trophies={ featuredAwards } />
          </div>

          <div className={ `${styles.marketHomePage__section} ${styles.marketHomePage__organizersSection}` }>
            <FeaturedOrganizers
              organizers={ organizers }
              appearance="museumHome"
            />
          </div>

          <div className={ `${styles.marketHomePage__section} ${styles.marketHomePage__contactSection}` }>
            <MuseumContactBanner />
          </div>

          <div className={ `${styles.marketHomePage__section} ${styles.marketHomePage__faqSection}` }>
            <Faq appearance="museumHome" />
          </div>
        </div>

        <PolygonBanner appearance="museumHome" />
      </div>
    </>
  )
}
